import React, {useRef, useEffect} from 'react';
import {
  ChatAlt2Icon,
  ClipboardCheckIcon,
  DocumentTextIcon,
  SwitchHorizontalIcon,
  UsersIcon,
  SparklesIcon,
} from '@heroicons/react/outline';

import Button, {ButtonSizes} from '../components/ButtonNew';
import DocumentHeader from '../components/DocumentHeader';
import Link from '../components/Link';
import Stars from '../components/Stars';
import {TestimonialSection} from '../components/TestimonialSection';
import YouTubeVideo from '../components/YouTubeVideo';
import {LANDING_VIDEO} from '../constants/coaching';

import {useAnalytics} from '../hooks';
import {ShortRightArrowIcon} from '../components/svg';

const ServiceCard = ({
  icon,
  name,
  href,
  onClick,
}) => (
  <Link
    className="p-5 md:px-6 rounded-lg bg-white border border-gray-200 shadow hover:shadow-md transition duration-50 flex flex-col shrink-0 items-center justify-between w-36 lg:w-full"
    href={href}
    onClick={() => onClick({name})}
  >
    <span className="text-indigo-500 w-6 mt-1">{icon}</span>
    <span className="my-3 font-semibold text-sm md:text-base text-gray-800 text-center leading-tight">{name}</span>
    <ShortRightArrowIcon />
  </Link>
);

const CoachingLandingPage = () => {
  const {tracker} = useAnalytics();
  const servicesRef = useRef();

  useEffect(() => {
    tracker.coachingLandingView();
  }, []);

  const onClickCTA = () => {
    servicesRef.current.scrollIntoView({behavior: 'smooth'});
    tracker.coachingLandingClickCta();
  };

  return (
    <div className="relative bg-white overflow-hidden">
      <DocumentHeader
        title="Coaching and Mock Interviews for Your Tech Job"
        socialDescription="Connect with an expert in your field to practice mock interviews, review your resume, or create a study plan."
      />
      <div className="relative pb-12 sm:pb-16">
        <div className="mt-8 mx-auto max-w-6xl px-4 sm:mt-16 grid grid-cols-12">
          <div className="text-center col-span-12 md:text-left md:col-span-6 pt-6">
            <span className="md:hidden inline-flex mb-2 uppercase tracking-wider text-indigo-600 mx-auto font-semibold">Coaching</span>
            <h1 className="mx-auto tracking-tight font-extrabold text-gray-900 text-2xl sm:text-3xl md:text-5xl">
              Get expert coaching and mock interviews
            </h1>
            <div className="mt-5 max-w md:mt-8">
              <Button
                size={ButtonSizes.LARGE}
                className="px-5 w-full sm:w-auto"
                onClick={onClickCTA}
              >
                Find your coach
              </Button>
            </div>
            <div className="mt-6 flex flex-col items-center justify-center md:items-baseline">
              <h2 className="font-medium text-sm text-gray-500">
                Work with industry experts from companies like
              </h2>
              <div className="pt-1.5 flex gap-1">
                <img className="h-5" alt="Google Logo" src="/images/companies/google_logo.png" />
                <img className="h-5" alt="Meta Logo" src="/images/companies/meta_logo.png" />
                <img className="h-5" alt="Airbnb Logo" src="/images/companies/airbnb_logo.png" />
                <img className="h-5" alt="Amazon Logo" src="/images/companies/amazon_logo.png" />
              </div>
            </div>
          </div>
          <div className="hidden ml-12 col-span-6 pt-6 md:block">
            <div className="w-full overflow-hidden rounded-xl">
              <YouTubeVideo videoID={LANDING_VIDEO} />
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col py-4 py-8 bg-gray-100" ref={servicesRef}>
        <span className="text-lg md:text-2xl text-black font-bold text-center mb-2">
          What do you need help with in your career?
        </span>
        <span className="text-base text-gray-600 font-medium text-center">
          Work with a vetted expert who can help prepare and guide you through
          your biggest career challenges.
        </span>
        <div className="mt-6 px-4 pb-2 flex flex-row lg:max-w-6xl lg:mx-auto lg:grid lg:grid-cols-5 space-x-2 lg:space-x-0 lg:gap-x-4 gap-y-5 overflow-scroll scrollbar-hidden">
          <ServiceCard
            name="Mock Interviews"
            href="/coaching?category=mock_interviews&src=landing-interviews"
            icon={<ClipboardCheckIcon />}
            onClick={tracker.coachingLandingClickService}
          />
          <ServiceCard
            name="Resume Reviews"
            href="/resume?src=landing-resume"
            icon={<DocumentTextIcon />}
            onClick={tracker.coachingLandingClickService}
          />
          <ServiceCard
            name="Career Coaching"
            href="/coaching?category=career_coaching&src=landing-roles"
            icon={<UsersIcon />}
            onClick={tracker.coachingLandingClickService}
          />
          <ServiceCard
            name="Negotiation"
            href="/coaching?category=negotiation&src=landing-negotiation"
            icon={<SwitchHorizontalIcon />}
            onClick={tracker.coachingLandingClickService}
          />
          <ServiceCard
            name="Communication Coaching"
            href="/coaching?category=communication_skills"
            icon={<ChatAlt2Icon />}
            onClick={tracker.coachingLandingClickService}
          />
        </div>
        <span className="mt-4 font-medium text-base text-center">
          Just exploring?
          {' '}
          <Link href="/coaching?src=landing-browse-all" className="text-indigo-600">
            Browse all our available coaches
            <ShortRightArrowIcon className="ml-2 h-3 w-3 text-indigo-600" />
          </Link>
        </span>
      </div>
      <div className="relative overflow-hidden bg-white pt-16 pb-32">
        <div className="relative">
          <div className="lg:mx-auto lg:grid lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-2 lg:gap-24 lg:px-8">
            <div className="mx-auto max-w-xl px-4 sm:px-6 lg:mx-0 lg:max-w-none lg:py-16 lg:px-0">
              <div>
                <div>
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-indigo-600">
                    <SparklesIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </span>
                </div>
                <div className="mt-6">
                  <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                    Take the guesswork out of interviewing
                  </h2>
                  <p className="mt-4 text-lg text-gray-500">
                    Don&rsquo;t waste countless hours searching for the right resources.
                    Practice mock interviews and get personalized feedback directly from experienced
                    interviewers at top tech companies.
                  </p>
                  <div className="mt-6">
                    <Button size={ButtonSizes.LARGE} href="/coaching?category=mock_interviews&src=landing-interviewing-cta">
                      Get started
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-12 sm:mt-16 lg:mt-0">
              <div className="hidden lg:block lg:relative h-full">
                <img className="" alt="" src="/images/coaches/landing_page_3.png" />
              </div>
            </div>
          </div>
        </div>
        <div className="mt-24">
          <div className="lg:mx-auto lg:grid lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-2 lg:gap-24 lg:px-8">
            <div className="mx-auto max-w-xl px-4 sm:px-6 lg:col-start-2 lg:mx-0 lg:max-w-none lg:py-32 lg:px-0">
              <div>
                <div>
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-indigo-600">
                    <SparklesIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </span>
                </div>
                <div className="mt-6">
                  <h2 className="text-3xl font-bold tracking-tight text-gray-900">Invest in your career long-term</h2>
                  <p className="mt-4 text-lg text-gray-500">
                    Work with a vetted coach to highlight your strengths on your
                    applications, land more interviews, and find the job of your dreams.
                    Go beyond your job search with career coaching and skill development.
                  </p>
                  <div className="mt-6">
                    <Button size={ButtonSizes.LARGE} href="/coaching?category=mock_interviews&src=landing-career-cta">
                      Get started
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-12 sm:mt-16 lg:col-start-1 lg:mt-0">
              <div className="hidden lg:block lg:relative h-full">
                <img className="" alt="" src="/images/coaches/landing_page_2.png" />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col justify-center items-center py-4 py-8">
          <span className="text-lg md:text-2xl text-black font-bold text-center mb-2">
            What people are saying
          </span>
          <div className="mb-6">
            <Stars rating="4.8 rating from over 2,400 reviews" />
          </div>
          <TestimonialSection showHeader={false} />
        </div>
      </div>
    </div>
  );
};

export default CoachingLandingPage;
